import React from 'react'
import { useLocation } from '@reach/router'
import { initializeAndTrack } from 'gatsby-plugin-gdpr-cookies'
import { TranslatedLink } from './TranslatedLink'
import LinkBtn from './linkBtn/LinkBtn'
import Modal from './pages/modal/Modal'
import SwitchInput from './inputs/SwitchInput'
import { isBrowser, getTranslateFunction } from '../utilities'

const HIDE_COOKIE_BAR_LS_KEY = 'narative-hide-cookie-bar'
const GOOGLE_TAG_MANAGER_KEY = 'narative-gdpr-google-tagmanager'
const PIXEL_KEY = 'narative-2-gdpr-facebook-pixel'
const COOKIE_VISIBILITY_CLASS = 'cookie-consent-hidden'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

const parseStoredValue = (rawValue) => {
  if (rawValue == null || rawValue === '') {
    return null
  }

  if (rawValue === 'true') {
    return true
  }

  if (rawValue === 'false') {
    return false
  }

  try {
    const parsedValue = JSON.parse(rawValue)
    return typeof parsedValue === 'boolean' ? parsedValue : null
  } catch (error) {
    return null
  }
}

const getStoredCookieValue = (key) => {
  if (!isBrowser()) {
    return null
  }

  const match = document.cookie.match(new RegExp(`(?:^|; )${key}=([^;]*)`))
  return match ? parseStoredValue(decodeURIComponent(match[1])) : null
}

const getStoredValue = (key) => {
  if (!isBrowser()) {
    return null
  }

  let localStorageValue = null

  try {
    localStorageValue = parseStoredValue(window.localStorage.getItem(key))
  } catch (error) {
    localStorageValue = null
  }

  if (localStorageValue != null) {
    return localStorageValue
  }

  return getStoredCookieValue(key)
}

const setStoredValue = (key, value) => {
  if (!isBrowser()) {
    return
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    // Ignore storage write failures and fall back to cookies.
  }

  document.cookie = `${key}=${value};path=/;max-age=${COOKIE_MAX_AGE};SameSite=Lax`
}

const syncCookieBarVisibility = (isHidden) => {
  if (!isBrowser()) {
    return
  }

  document.documentElement.classList.toggle(COOKIE_VISIBILITY_CLASS, isHidden)
}

const enableGtag = () => {
  if (!isBrowser()) {
    return
  }

  window.dataLayer = window.dataLayer || []

  function gtag() {
    window.dataLayer.push(arguments)
  }

  gtag('consent', 'update', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted',
  })
}

const loadGtag = () => {
  if (!isBrowser()) {
    return
  }

  window.dataLayer = window.dataLayer || []

  function gtag() {
    window.dataLayer.push(arguments)
  }

  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  })
}

const getInitialCookieManagerHidden = () => getStoredValue(HIDE_COOKIE_BAR_LS_KEY) === true

const getInitialTagManagerValue = () => {
  const storedValue = getStoredValue(GOOGLE_TAG_MANAGER_KEY)
  return storedValue == null ? true : storedValue
}

const CookieBar = ({ lang = 'sk' }) => {
  const slug = lang === 'en' ? '/en/privacy-policy' : '/zasady-spracuvania-osobnych-udajov/'
  const t = getTranslateFunction(lang)
  const location = useLocation()
  const modalRef = React.useRef(null)
  const [cookieManagerHidden, setCookieManagerHidden] = React.useState(getInitialCookieManagerHidden)
  const [tagManagerValue, setTagManagerValue] = React.useState(getInitialTagManagerValue)
  const [preferencesReady, setPreferencesReady] = React.useState(false)

  const enable = React.useCallback(() => {
    if (tagManagerValue) {
      enableGtag()
    } else {
      loadGtag()
    }

    setStoredValue(HIDE_COOKIE_BAR_LS_KEY, true)
    setStoredValue(GOOGLE_TAG_MANAGER_KEY, tagManagerValue)
    setStoredValue(PIXEL_KEY, tagManagerValue)
    syncCookieBarVisibility(true)
    setCookieManagerHidden(true)
  }, [tagManagerValue])

  const onClickChild = React.useCallback(() => {
    modalRef.current?.showModal()
  }, [])

  const onSubmitCookiesForm = React.useCallback(() => {
    enable()
  }, [enable])

  React.useEffect(() => {
    if (!isBrowser()) {
      return undefined
    }

    initializeAndTrack(location)
  }, [location])

  React.useEffect(() => {
    if (!isBrowser()) {
      return undefined
    }

    const storedCookieConsent = getStoredValue(HIDE_COOKIE_BAR_LS_KEY)
    const storedTagManagerConsent = getStoredValue(GOOGLE_TAG_MANAGER_KEY)

    if (storedCookieConsent != null) {
      setCookieManagerHidden(storedCookieConsent)
    }

    if (storedTagManagerConsent != null) {
      setTagManagerValue(storedTagManagerConsent)
    }

    if (storedCookieConsent === true && storedTagManagerConsent !== false) {
      enableGtag()
    } else {
      loadGtag()
    }

    setPreferencesReady(true)

    const handleShowCookieBar = () => {
      modalRef.current?.showModal()
    }

    document.addEventListener('showCookieBar', handleShowCookieBar)

    return () => {
      document.removeEventListener('showCookieBar', handleShowCookieBar)
    }
  }, [])

  React.useEffect(() => {
    if (!preferencesReady) {
      return
    }

    syncCookieBarVisibility(cookieManagerHidden)
  }, [cookieManagerHidden, preferencesReady])

  return (
    <>
      {!cookieManagerHidden && (
        <>
          <div className="cookie-bg"></div>
          <div className="cookie-bar">
            <div className="container">
              <div className="row">
                <div className="col-12 col-lg-6 col-xl-7">
                  <span dangerouslySetInnerHTML={{ __html: t('cookieBar.Text1') }}></span>
                  <TranslatedLink lang={lang} to={slug}>
                    {t('cookieBar.Text2')}
                  </TranslatedLink>
                  .
                </div>
                <div className="col-auto mr-auto ml-auto mr-lg-0 pt-2 pt-md-0 d-flex align-items-center">
                  <LinkBtn className="loaded" showBg={true} handleClick={enable}>
                    <div>{t('cookieBar.BtnSubmit')}</div>
                  </LinkBtn>
                  <LinkBtn className="loaded ml-5 text-1" color="silver">
                    <span onClick={onClickChild}>{t('cookieBar.BtnEdit')}</span>
                  </LinkBtn>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      <Modal
        className="cookie-bar-modal"
        closeBtn={t('cookieBar.Cancel')}
        submitBtn={t('cookieBar.Save')}
        size="lg"
        onSubmitHandle={onSubmitCookiesForm}
        ref={modalRef}
        showHeader={false}
      >
        <div className="row">
          <div className="col-12 mt-md-3">
            <h4 className="mb-1 mb-md-3">
              <span dangerouslySetInnerHTML={{ __html: t('cookieBar.NesesseryTitle') }}></span>
            </h4>
            <SwitchInput defaultValue={true} disabled={true}>
              <span dangerouslySetInnerHTML={{ __html: t('cookieBar.NesesseryText') }}></span>
            </SwitchInput>
          </div>
          <div className="col-12 mt-3 mt-md-5">
            <h4 className="mb-1 mb-md-3">
              <span dangerouslySetInnerHTML={{ __html: t('cookieBar.OptionalTitle') }}></span>
            </h4>
            <SwitchInput defaultValue={tagManagerValue} onChange={setTagManagerValue}>
              <span dangerouslySetInnerHTML={{ __html: t('cookieBar.OptionalText') }}></span>
            </SwitchInput>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default CookieBar
